import { Module } from './base';
import { getTranslation } from "../components/i18n";
import SliderCreator from "../components/slider-creator";

type CursorSize = 'L' | 'XL' | 'XXL';

// Import all SVG files
import cursorL from '../assets/cursors/macOS/L/cursor.svg';
import cursorXL from '../assets/cursors/macOS/XL/cursor.svg';
import cursorXXL from '../assets/cursors/macOS/XXL/cursor.svg';
import textCursorL from '../assets/cursors/macOS/L/textcursor.svg';
import textCursorXL from '../assets/cursors/macOS/XL/textcursor.svg';
import textCursorXXL from '../assets/cursors/macOS/XXL/textcursor.svg';
import pointingHandL from '../assets/cursors/macOS/L/pointinghand.svg';
import pointingHandXL from '../assets/cursors/macOS/XL/pointinghand.svg';
import pointingHandXXL from '../assets/cursors/macOS/XXL/pointinghand.svg';
import notAllowedL from '../assets/cursors/macOS/L/notallowed.svg';
import notAllowedXL from '../assets/cursors/macOS/XL/notallowed.svg';
import notAllowedXXL from '../assets/cursors/macOS/XXL/notallowed.svg';

// Create a map of all cursor SVGs
const cursorSvgs: Record<CursorSize, {
  cursor: string;
  text: string;
  pointing: string;
  notAllowed: string;
}> = {
  'L': {
    cursor: cursorL,
    text: textCursorL,
    pointing: pointingHandL,
    notAllowed: notAllowedL
  },
  'XL': {
    cursor: cursorXL,
    text: textCursorXL,
    pointing: pointingHandXL,
    notAllowed: notAllowedXL
  },
  'XXL': {
    cursor: cursorXXL,
    text: textCursorXXL,
    pointing: pointingHandXXL,
    notAllowed: notAllowedXXL
  }
};

class CursorSizeModule implements Module {
  name = 'cursor-size';

  setup(a11ySettings: Record<string, any>, modulesContainer: HTMLElement) {
    const moduleName = this.name;
    let styleContents = `
      :root {
        --accessibility-cursor-size: 1;
      }
    `;

    const os = 'macOS';
    const sizes: CursorSize[] = ['L', 'XL', 'XXL'];
    const offsets: Record<string, Record<string, number[]>> = {
      'L': {
        'cursor': [16, 10],
        'text': [26, 26],
        'pointer': [19, 12],
        'notallowed': [14, 7]
      },
      'XL': {
        'cursor': [18, 12],
        'text': [30, 30],
        'pointer': [22, 14],
        'notallowed': [16, 7]
      },
      'XXL': {
        'cursor': [20, 14],
        'text': [34, 34],
        'pointer': [25, 16],
        'notallowed': [18, 7]
      }
    };

    sizes.forEach((size) => {
      styleContents += `
    .cursor-${size} {
      a, 
      input[type="radio"], 
      input[type="checkbox"], 
      input[type="range"], 
      label:has(input[type="radio"]), 
      label:has(input[type="checkbox"]), 
      label:has(input[type="range"]),
      label:has(input[type="radio"]) *, 
      label:has(input[type="checkbox"]) *, 
      label:has(input[type="range"]) *,
      button, select, button > *, a > * {
        cursor: url('${cursorSvgs[size].pointing}') ${offsets[size]['pointer'].join(" ")}, auto !important;
      }

      h1, h2, h3, h4, h5, span:not(label:has(input) *):not(button *):not(a *), p, input, textarea {
        cursor: url('${cursorSvgs[size].text}') ${offsets[size]['text'].join(" ")}, auto !important;
      }

      input[type="range"]::-webkit-slider-thumb {
        cursor: url('${cursorSvgs[size].pointing}') ${offsets[size]['pointer'].join(" ")}, auto !important;
      }

      input[type="range"]::-moz-range-thumb {
        cursor: url('${cursorSvgs[size].pointing}') ${offsets[size]['pointer'].join(" ")}, auto !important;
      }

      :disabled, .disabled {
        cursor: url('${cursorSvgs[size].notAllowed}') ${offsets[size]['notallowed'].join(" ")}, auto !important;
      }

      * {
        cursor: url('${cursorSvgs[size].cursor}') ${offsets[size]['cursor'].join(" ")}, auto !important;
      }
    }`
    });

    let sliderValue = 1;
    if (a11ySettings[moduleName]) {
      sliderValue = a11ySettings[moduleName];
    }

    const cursorSizeElement = document.createElement("div");
    cursorSizeElement.className = `a11y-module a11y-module-${moduleName}`;
    cursorSizeElement.innerHTML = `
      <label id="a11y-${moduleName}-label" for="a11y-${moduleName}" data-translation-field="true" data-translation-key="cursorSize">${getTranslation('cursorSize')}</label>
    ` + SliderCreator.createSlider(moduleName, 1, 4, 1, sliderValue, 1);
    modulesContainer.appendChild(cursorSizeElement);

    SliderCreator.createSliderListeners(moduleName);
    return styleContents;
  }

  reset() {
    document.documentElement.style.setProperty('--accessibility-cursor-size', '1');
    const slider = document.getElementById('a11y-cursor-size') as HTMLInputElement;
    if (slider) {
      slider.value = '1';
      slider.dispatchEvent(new Event('input'));
    }
  }
}

export default new CursorSizeModule();